// src/pages/Home/HomeFeaturesOnboarding.jsx
import React, { useMemo, useRef, useState, useEffect } from "react";
import { useUser } from "./UserContext";
import { getRoleFromRoleId } from "../../utils/roleHelper";
import { sendNotificationToAdmin } from "../../services/notificationServices";
import { FaChevronDown, FaPaperPlane, FaCheck } from "react-icons/fa";

/* -------------------------------------------------
   Rôles (0 = Visiteur, non connecté)
-------------------------------------------------- */
const ROLES = [
  {
    id: 0,
    label: "Visiteur",
    badgeClass:
      "bg-slate-100 text-slate-800 ring-slate-200 dark:bg-slate-800 dark:text-slate-200",
    items: [
      "Découvrir la Ligue et ses commissions.",
      "Consulter la carte des clubs de Nouvelle-Aquitaine.",
      "Consulter les infos du Centre National.",
      "S'inscrire sur les cours du Centre National.",
      "Consulter les calendriers des compétitions (Ligue & Comités).",
      "Consulter les résultats des compétitions.",
      "Consulter les catégories d'âge/poids.",
    ],
  },
  {
    id: 2,
    label: "Utilisateur connecté",
    badgeClass:
      "bg-emerald-100 text-emerald-800 ring-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-200",
    items: [
      "Modification de son profil",
      "Demande de changement de rôle en fonction de vos besoins.",
      "Lier son compte à un club de Nouvelle-Aquitaine.",
      "Accès complet aux ressources techniques, kata, vidéos.",
      "Quizz techniques pour s’évaluer.",
      "Recherche de partenaires de Kata.",
      "Règlements arbitrage.",
    ],
  },
  {
    id: 8,
    label: "Enseignant · Coach",
    badgeClass:
      "bg-cyan-100 text-cyan-800 ring-cyan-200 dark:bg-cyan-900/40 dark:text-cyan-200",
    items: [
      "Dépôt du badge numérique de formation contre les violences sexuelles.",
      "Consultation des petites annonces dédiées aux enseignants et au matériel.",
      "Exportation excel des compétitions filtrées",
      "Tableau de scores compétition (à venir)",
    ],
  },
  {
    id: 3,
    label: "Dirigeant de Club",
    badgeClass:
      "bg-blue-100 text-blue-800 ring-blue-200 dark:bg-blue-900/40 dark:text-blue-200",
    items: [
      "Dépôt du badge numérique de formation contre les violences sexuelles.",
      "Création de petites annonces pour les enseignants et le matériel.",
      "Possibilité de répondre à des votes électroniques ou sondages.",
    ],
  },
  {
    id: 4,
    label: "Arbitrage",
    badgeClass:
      "bg-amber-100 text-amber-800 ring-amber-200 dark:bg-amber-900/40 dark:text-amber-200",
    items: [
      "Règlements d’arbitrage à jour (toutes disciplines).",
      "Auto-formation des arbitres avec des quizzes techniques.",
      "Statistiques de performance.",
    ],
  },
  {
    id: 5,
    label: "Responsable Arbitrage",
    badgeClass:
      "bg-violet-100 text-violet-800 ring-violet-200 dark:bg-violet-900/40 dark:text-violet-200",
    items: [
      "Supervision des contenus et formations arbitrage.",
      "Diffusion de documents, consignes et mises à jour.",
      "Suivi des évaluations (quizz) et coordination régionale.",
    ],
  },
  {
    id: 6,
    label: "Responsable Comité Départemental & CTF",
    badgeClass:
      "bg-rose-100 text-rose-800 ring-rose-200 dark:bg-rose-900/40 dark:text-rose-200",
    items: [
      "Participation aux votes électroniques lors d'AG (Président(e) de comité).",
      "Accès aux outils administrateurs.",
      "Gestion des clubs, compétitions, résultats au niveau départemental.",
      "Consultation des badges numériques pour les coachs.",
    ],
  },
  {
    id: 7,
    label: "Membre de la Ligue NA",
    badgeClass:
      "bg-fuchsia-100 text-fuchsia-800 ring-fuchsia-200 dark:bg-fuchsia-900/40 dark:text-fuchsia-200",
    items: [
      "Accès aux outils administrateurs.",
      "Création des votes électroniques pour les assemblées générales.",
      "Historique et affichage des résultats.",
    ],
  },
];

/* -------------------------------------------------
   Élément d’accordéon (avec bouton Demander ce rôle)
-------------------------------------------------- */
function AccordionItem({
  role,
  expanded,
  onToggle,
  isConnected,
  isCurrent,
  onRequestRole,
  state, // "idle" | "sending" | "sent"
}) {
  const panelRef = useRef(null);
  const [maxH, setMaxH] = useState("0px");

  useEffect(() => {
    if (!panelRef.current) return;
    const contentH = panelRef.current.scrollHeight || 0;
    setMaxH(expanded ? `${contentH}px` : "0px");
  }, [expanded, role.items]);

  return (
    <div
      className={[
        "rounded-2xl backdrop-blur",
        "bg-white/85 dark:bg-slate-900/60",
        "ring-1 ring-slate-200 dark:ring-white/10",
        "shadow-sm hover:shadow-lg transition-shadow",
      ].join(" ")}
    >
      <button
        type="button"
        aria-expanded={expanded}
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-4 px-5 py-4"
      >
        <div className="flex items-center gap-3">
          <span
            className={[
              "inline-flex items-center px-2.5 py-1 rounded-lg text-normal font-semibold ring-1",
              role.badgeClass,
            ].join(" ")}
          >
            {role.label}
          </span>

          {isCurrent && (
            <span className="px-2 py-0.5 rounded-full text-[11px] bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200 ring-1 ring-emerald-200">
              Rôle actuel
            </span>
          )}
          {!isConnected && role.id !== 0 && (
            <span className="px-2 py-0.5 rounded-full text-[11px] bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200 ring-1 ring-slate-200">
              Connectez-vous pour demander
            </span>
          )}
        </div>

        <FaChevronDown
          className={[
            "text-slate-600 dark:text-slate-300 transition-transform duration-300",
            expanded ? "rotate-180" : "",
          ].join(" ")}
        />
      </button>

      {/* Panneau */}
      <div
        ref={panelRef}
        style={{ maxHeight: maxH }}
        className="overflow-hidden transition-[max-height] duration-400 ease-in-out"
        aria-hidden={!expanded}
      >
        <div className="px-5 pb-5 pt-0">
          <ul className="mt-1 space-y-2 text-sm text-slate-700 dark:text-slate-200 list-disc list-inside">
            {role.items.map((txt, i) => (
              <li key={i}>{txt}</li>
            ))}
          </ul>

          {/* Bouton Demander ce rôle */}
          {isConnected && role.id !== 0 && (
            <div className="mt-4 flex justify-end">
              <button
                onClick={onRequestRole}
                disabled={isCurrent || state === "sending" || state === "sent"}
                className={[
                  "inline-flex items-center gap-2 px-4 py-2 rounded-xl transition ring-1",
                  isCurrent
                    ? "bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300 ring-slate-300 cursor-not-allowed"
                    : state === "sent"
                    ? "bg-emerald-600 text-white ring-emerald-500"
                    : "bg-slate-900 text-white hover:bg-slate-700 ring-slate-800",
                ].join(" ")}
                title={
                  isCurrent
                    ? "C’est déjà votre rôle"
                    : state === "sent"
                    ? "Demande envoyée"
                    : "Envoyer une demande aux administrateurs"
                }
              >
                {state === "sent" ? <FaCheck /> : <FaPaperPlane />}
                {isCurrent
                  ? "Rôle en cours"
                  : state === "sending"
                  ? "Envoi…"
                  : state === "sent"
                  ? "Demande envoyée"
                  : "Demander ce rôle"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------
   Composant principal
-------------------------------------------------- */
export default function HomeFeaturesOnboarding() {
  const { user } = useUser();
  const currentRoleId = user?.role_id ?? 0;
  const currentRoleLabel = useMemo(
    () => (currentRoleId ? getRoleFromRoleId(currentRoleId) : "Visiteur"),
    [currentRoleId]
  );

  // Accordéon contrôlé : un seul ouvert à la fois
  const [openId, setOpenId] = useState(null);
  const toggle = (id) => setOpenId((prev) => (prev === id ? null : id));

  // État des demandes par rôle
  // { [roleId]: "idle" | "sending" | "sent" }
  const [requestState, setRequestState] = useState({});

  const handleRequestRole = async (roleId, roleLabel) => {
    if (!user?.userId) return;
    try {
      setRequestState((s) => ({ ...s, [roleId]: "sending" }));
      const nom = user?.nom || "";
      const prenom = user?.prenom || "";
      // Message EXACT comme convenu précédemment
      const message = `${nom} ${prenom}  souhaite changer de rôle pour ${roleLabel}`;
      await sendNotificationToAdmin(message, user.userId);
      setRequestState((s) => ({ ...s, [roleId]: "sent" }));
    } catch (e) {
      console.error("Erreur envoi notification admin:", e);
      // Revenir à idle pour retenter
      setRequestState((s) => ({ ...s, [roleId]: "idle" }));
      alert("Une erreur est survenue lors de l’envoi. Réessayez plus tard.");
    }
  };

  return (
    <section className="bg-gradient-to-b from-slate-50 to-slate-200 dark:from-gray-900 dark:to-gray-800 py-14 px-6 font-satoshi">
      {/* Bandeau rôle actuel */}
      <div className="max-w-[1100px] mx-auto text-center mb-8">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/80 dark:bg-slate-800/70 ring-1 ring-slate-200 dark:ring-white/10">
          <span className="text-normal text-slate-700 dark:text-slate-200">
            Votre rôle actuel :
          </span>
          <span className="text-normal font-semibold text-slate-900 dark:text-white">
            {user ? currentRoleLabel : "Visiteur (non connecté)"}
          </span>
        </div>
        <p className="mt-3 text-normal text-slate-600 dark:text-slate-300">
          Dépliez un rôle pour découvrir, en quelques points, ses
          fonctionnalités. Vous pouvez envoyer une demande de changement
          directement depuis l’accordéon (si connecté).
        </p>
      </div>

      {/* Liste des rôles en accordéon */}
      <div className="max-w-[1100px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-5">
        {ROLES.map((r) => (
          <AccordionItem
            key={r.id}
            role={r}
            expanded={openId === r.id}
            onToggle={() => toggle(r.id)}
            isConnected={!!user}
            isCurrent={r.id === currentRoleId}
            state={requestState[r.id] || "idle"}
            onRequestRole={() => handleRequestRole(r.id, r.label)}
          />
        ))}
      </div>

      {/* Légende subtile */}
      <div className="max-w-[1100px] mx-auto mt-6 text-center text-xs text-slate-500 dark:text-slate-400">
        Si vous souhaitez changer de rôle, une demande sera envoyée aux
        administrateurs pour validation.
      </div>
    </section>
  );
}
